import {
  AfterViewInit,
  OnDestroy,
  inject,
  Input,
  Directive,
} from '@angular/core';
import { MatOption, MatSelect } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';

/**
 * Directive to add `Select All` functionality to a `MatSelect` with multiple selection.
 *
 * - Provides a dedicated option that lets users select or deselect all available options at once.
 * - Ensures the `select all` option itself is `never included` in the actual form value.
 * - Keeps the `select-all` option visually synced with the current selection state.
 * - Generic and reusable for any array of items with customizable value keys.
 */
@Directive({
  selector: '[appSelectAll]',
  standalone: true,
})
export class SelectAllDirective<T> implements AfterViewInit, OnDestroy {
  /**
   *  Array of all available options, e.g. all items
   */
  @Input({ required: true }) allValues: T[] = [];

  /**
   * Property key of T to use as value, e.g. 'id'. Only needs to specified if allValues is an array of objects.
   */
  @Input({ required: false }) valueIdentifier!: keyof T;

  private readonly _destroy$: Subject<void> = new Subject();

  /* Reference to the surrounding MatSelect */
  private readonly _matSelect = inject(MatSelect);
  /* Reference to the MatOption this directive is attached to (the select-all option) */
  private readonly _matOption = inject(MatOption);

  /* Internal marker value to identify the select-all option, excluded from form value */
  private readonly _selectAllMarker = '__select_all__';

  ngAfterViewInit(): void {
    /* Shortcut to MatSelect */
    const parentSelect = this._matSelect;

    /* FormControl of the MatSelect */
    const parentFormControl = parentSelect.ngControl.control;

    /* Abort if no FormControl is present */
    if (!parentFormControl) return;

    /* Assign the marker value to the select-all option */
    this._matOption.value = this._selectAllMarker;

    /* Listen to selection changes on the select-all option */
    this._matOption.onSelectionChange
      .pipe(takeUntil(this._destroy$))
      .subscribe(ev => {
        if (ev.isUserInput) {
          if (ev.source.selected) {
            /* When select-all is chosen, set all IDs */
            parentFormControl?.setValue(
              this.valueIdentifier
                ? this.allValues.map(val => val[this.valueIdentifier])
                : this.allValues,
              { emitEvent: false }
            );
            /* Deselect the select-all option itself to avoid including its value */
            this._matOption.select(false);
          } else {
            /* When select-all is deselected, clear all selections */
            parentFormControl?.setValue([]);
            this._matOption.deselect(false);
          }
        }
      });

    /* Listen to any option selection changes */
    parentSelect.optionSelectionChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        /* Get current form value as an array */
        const values = (parentFormControl.value ?? []) as Array<T[keyof T]>;

        /* Filter out the internal select-all marker from the value */
        const filtered = values.filter(
          value =>
            value !== this._selectAllMarker &&
            this.allValues.some(item =>
              this.valueIdentifier
                ? item[this.valueIdentifier] === value
                : item === value
            )
        );

        /* Only update form value if the filtered array differs from current */
        if (filtered.length !== values.length) {
          parentFormControl.setValue(filtered);
        }

        /** Sync the select-all option’s selected state:
         *  - select it if all options are selected
         *  - deselect otherwise
         */
        if (filtered.length === this.allValues.length) {
          this._matOption.select(false);
        } else {
          this._matOption.deselect(false);
        }
      });
  }

  /* Cleanup subscriptions on directive destroy */
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
